import App from 'antd/lib/app';
import { useLocation, useParams } from 'react-router-dom';
import { usePageLocation } from '../usePageState';
import { useDocumentTitle } from '../../components/useDocumentTitle';
import { useState } from 'react';
import { useAuth } from '../../domain/auth/useAuth';
import { DEFAULT_PAGINATION } from '../../domain/commonConst';
import { IPaginationMeta } from '../../domain/IPagination';
import { getTablePaginationParams } from '../../components/table/getTablePaginationParams';
import { useEventAnalyticGroupedBySiteListQuery } from '../../domain/event-analytic/queries';
import { toUsefulParams } from '../toUsefulParams';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import {
    DownloadEventPerformanceCsvModal,
    IDownloadEventPerformanceCsvFormData,
} from './downloadEventPerformanceModal/DownloadEventPerformanceModal';

import './event-performance.css';
import {
    IEventAnalyticGroupedBySite,
    IEventAnalyticEnergyUsageParams,
    IEventAnalytic,
    IFetchEventAnalyticRequestParams,
} from '../../domain/event-analytic/interface';
import { TablePaginationConfig } from 'antd/lib/table';
import { TableColumnsType } from 'antd/lib';
import { getColumnSortOrder } from '../../components/table/columnFormatHelpers';
import { WithEmptyDataTable } from '../../components/table/withEmptyDataTable';
import Table from 'antd/lib/table';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import { PageHeader } from '../../components/PageHeader';
import { ResizableColumnTitle } from '../../components/table/resizableColumnTitle';
import { exportEventAnalyticEnergyUsage, exportEventAnalytic } from '../../domain/event-analytic';
import { EventAnalyticGroupedBySiteTableTitle } from '../../components/EventAnalytic/EventAnalyticGroupedBySiteTableTitle';
import { SiteAddress } from '../../components/site/SiteAddress';
import { ExtraSiteAnalyticInfo } from '../../components/EventAnalytic/ExtraSiteAnalyticInfo';
import Dropdown from 'antd/lib/dropdown';
import { metricSorter } from '../../domain/event-analytic/metricSorter';
import { displayMetric } from '../../domain/event-analytic/displayMetric';
import Tooltip from 'antd/lib/tooltip';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import { SECONDARY_COLOR } from '../../theme';
import { IPerformanceChartModalConfig, PerformanceChartModal } from './performanceChartModal/PerformanceChartModal';
import { resolveBaselineMarketFromMarkets } from '../../domain/energy/baselineMarket';
import { ActionList } from '../../components/table/actionList/actionList';
import { AreaChartOutlined } from '@ant-design/icons';
import { isNumber } from 'lodash';
import {
    HistoricalPerformanceChartModal,
    IHistoricalPerformanceChartModalConfig,
} from './historicalPerformanceChartModal/HistoricalPerformanceChartModal';
import { getProgramNameFromSource } from '../../domain/event/getProgramNameFromSource';
import HistoryOutlined from '@ant-design/icons/lib/icons/HistoryOutlined';

export interface IEventPerformanceBySitePageState {
    companyId?: number;
    pagination?: {
        pageSize: number;
        current: number;
    };
    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export const EventPerformanceBySite = () => {
    const { batchId } = useParams();
    const { notification } = App.useApp();
    const location = useLocation();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: IEventPerformanceBySitePageState = queryToState(location.search);

    const auth = useAuth()!;

    const isCustomerRoleType = auth.user?.isCustomer() || auth.user?.isOperator();

    const [csvDownloadPopupOpen, setCsvDownloadPopupOpen] = useState<boolean>(false);
    const [isExportLoading, setIsExportLoading] = useState(false);
    const [chartPopupConfig, setChartPopupConfig] = useState<IPerformanceChartModalConfig | null>(null);
    const [historyChartPopupConfig, setHistoryChartPopupConfig] = useState<IHistoricalPerformanceChartModalConfig | null>(null);

    useDocumentTitle('Sites Performance');
    const listQuery = {
        pagination: pageState?.pagination ?? DEFAULT_PAGINATION,
        batchId: batchId!,
        ...(pageState?.sorter && { sorter: pageState?.sorter }),
        ...(pageState?.companyId && { companyId: pageState?.companyId }),
    };

    const { data, isFetching, isError, error } = useEventAnalyticGroupedBySiteListQuery(listQuery, {
        keepPreviousData: true,
    });
    const eventAnalyticGroups = data?.data || [];

    if (isError) {
        notification.error({ key: 'event-analytics-bath-site-fetch-error', message: error?.message || 'Cannot fetch event analytics!' });
    }

    const meta =
        data?.meta ??
        ({
            total: 0,
            offset: 0,
            limit: pageState && pageState.pagination && pageState.pagination.pageSize ? pageState.pagination.pageSize : DEFAULT_PAGINATION.pageSize,
        } as IPaginationMeta);

    async function onTableChange(
        pagination: TablePaginationConfig,
        _: Record<string, FilterValue | null>,
        sorter: SorterResult<IEventAnalyticGroupedBySite> | SorterResult<IEventAnalyticGroupedBySite>[]
    ) {
        const preparedParams = toUsefulParams({ pagination, sorter });
        setPageQuery({ ...pageState, ...preparedParams });
    }

    const onCSVExport = async (options: Pick<IDownloadEventPerformanceCsvFormData, 'applyFilters'>) => {
        if (options.applyFilters && !eventAnalyticGroups.length) {
            notification.warning({
                key: 'no-data-for-csv-group-by-site',
                message: 'No data for selection.',
            });
            return;
        }

        setCsvDownloadPopupOpen(false);
        try {
            setIsExportLoading(true);
            let params: IFetchEventAnalyticRequestParams = {
                batchId: batchId!,
            };

            if (options.applyFilters) {
                params.companyId = pageState?.companyId;
                params.sorter = pageState?.sorter;
            }

            await exportEventAnalytic(params);
            setIsExportLoading(false);
        } catch (error) {
            notification.error({ key: 'event-analytics-site-csvdownload-error', message: 'Cannot download event analytics!' });
            setIsExportLoading(false);
        }
    };

    const onDownloadCsvClick = () => {
        // Customer/operator users are scoped to a single company, so "Apply Filters" vs "Download All" makes no difference
        if (isCustomerRoleType) {
            onCSVExport({ applyFilters: true });
            return;
        }

        setCsvDownloadPopupOpen(true);
    };

    const onEnergyUsageExport = async () => {
        if (!eventAnalyticGroups.length) {
            notification.warning({
                key: 'no-data-for-event-analytic-energy-csv',
                message: 'No data for selection.',
            });
            return;
        }
        try {
            setIsExportLoading(true);
            let params: IEventAnalyticEnergyUsageParams = {
                batchId: batchId!,
                companyId: pageState?.companyId,
                include: 'performance,market-prices',
            };

            await exportEventAnalyticEnergyUsage(params);
            setIsExportLoading(false);
        } catch (error) {
            notification.error({
                key: 'event-analytics-energy-csvdownload-error',
                message: `Cannot download energy usage data: ${(error as Error)?.message}`,
            });
            setIsExportLoading(false);
        }
    };

    const onChartViewOpen = (record: IEventAnalyticGroupedBySite, meterLevelData?: IEventAnalytic) => {
        if (!batchId) return;

        const meterLevelNomination = meterLevelData?.nomination_kw;
        const meterLevelAverageBaseline = meterLevelData?.baseline_kw;
        const baselineMarket = resolveBaselineMarketFromMarkets(record.market ? [record.market] : []);

        setChartPopupConfig({
            batchId,
            companyId: pageState?.companyId,
            eventStartUtc: record.event_datetime_start,
            eventDurationMinutes: record.event_duration,
            nomination: isNumber(meterLevelNomination) ? meterLevelNomination : record.nomination,
            averageBaseline: isNumber(meterLevelAverageBaseline) ? meterLevelAverageBaseline : record.baseline,
            meter_count: record.meters_count,
            siteId: record.site_id,
            siteName: record.site_name,
            meter: meterLevelData?.service_account_number,
            baselineMode: baselineMarket.mode,
            baselineWarning: baselineMarket.warning,
        });
    };

    const onHistoricalPerformanceViewOpen = (record: IEventAnalyticGroupedBySite, meterLevelData?: IEventAnalytic) => {
        const eventProgramName = getProgramNameFromSource(record.event_source);
        setHistoryChartPopupConfig({
            siteName: record.site_name,
            siteId: record.site_id,
            sourceEventId: record.event_id,
            programName: eventProgramName,
            san: meterLevelData?.service_account_number,
        });
    };

    const getActions = (item: IEventAnalyticGroupedBySite) => {
        return [
            ...(isNumber(item.baseline)
                ? [
                      {
                          title: () => 'Open Chart View',
                          onClick: (record: IEventAnalyticGroupedBySite) => () => onChartViewOpen(record),
                          icon: () => <AreaChartOutlined />,
                      },
                  ]
                : []),
            {
                title: () => 'Open Performance History',
                onClick: (record: IEventAnalyticGroupedBySite) => () => onHistoricalPerformanceViewOpen(record),
                icon: () => <HistoryOutlined />,
            },
        ];
    };

    const eventAnalyticColumns: TableColumnsType<IEventAnalyticGroupedBySite> = [
        {
            title: 'Company',
            dataIndex: 'company_name',
            key: 'company_name',
            sorter: (a: any, b: any) => a.company_name - b.company_name,
            ...getColumnSortOrder('company_name', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedBySite) => record.company_name,
        },
        {
            title: 'Site Name',
            dataIndex: 'site_name',
            key: 'site_name',
            sorter: (a: any, b: any) => a.site_name - b.site_name,
            ...getColumnSortOrder('site_name', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedBySite) => record.site_name,
        },
        {
            title: 'Store #',
            dataIndex: 'site_store_number',
            key: 'site_store_number',
            sorter: (a: any, b: any) => a.site_store_number - b.site_store_number,
            ...getColumnSortOrder('site_store_number', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedBySite) => record.site_store_number,
        },
        {
            title: 'Address',
            dataIndex: 'site_address',
            key: 'site_address',
            sorter: (a: any, b: any) => a.site_address - b.site_address,
            ...getColumnSortOrder('site_address', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedBySite) => (
                <SiteAddress address={record.site_address} city={record.site_city} state={record.site_state} />
            ),
        },
        {
            title: 'Meters',
            dataIndex: 'meters_count',
            key: 'meters_count',
            sorter: (a: any, b: any) => a.meters_count - b.meters_count,
            ...getColumnSortOrder('meters_count', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedBySite) => record.meters_count,
        },
        {
            title: 'Nomination (kW)',
            dataIndex: 'nomination',
            key: 'nomination',
            sorter: (a, b) => metricSorter(a.nomination, b.nomination),
            ...getColumnSortOrder('nomination', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedBySite) => displayMetric(record.nomination),
        },
        {
            title: 'Baseline (kW)',
            dataIndex: 'baseline',
            key: 'baseline',
            sorter: (a, b) => metricSorter(a.baseline, b.baseline),
            ...getColumnSortOrder('baseline', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedBySite) => displayMetric(record.baseline),
        },
        {
            title: (
                <>
                    <span>Performance (kW)</span>{' '}
                    <Tooltip title="Relative to Baseline">
                        <QuestionCircleOutlined style={{ color: SECONDARY_COLOR }} />
                    </Tooltip>
                </>
            ),
            dataIndex: 'performance',
            key: 'performance',
            sorter: (a, b) => metricSorter(a.performance, b.performance),
            ...getColumnSortOrder('performance', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedBySite) => displayMetric(record.performance),
            showSorterTooltip: false,
        },
        {
            title: 'Performance (% of kW)',
            dataIndex: 'performance_percentage',
            key: 'performance_percentage',
            sorter: (a, b) => metricSorter(a.performance_percentage, b.performance_percentage),
            ...getColumnSortOrder('performance_percentage', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedBySite) => displayMetric(record.performance_percentage, true),
        },
        {
            title: 'Data (% of kW)',
            dataIndex: 'data_completion',
            key: 'data_completion',
            sorter: (a, b) => metricSorter(a.data_completion, b.data_completion),
            ...getColumnSortOrder('data_completion', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedBySite) => displayMetric(record.data_completion, true),
        },
        {
            key: 'action',
            width: '120px',
            sorter: false,
            render: (_: string, record: IEventAnalyticGroupedBySite) => <ActionList actions={getActions(record)} item={record} />,
        },
    ];

    return (
        <>
            <div className="event--site-performance-page">
                <PageHeader
                    pageTitle={`Estimated Event Performance (${meta.total})`}
                    actions={[
                        <Dropdown.Button
                            size="large"
                            key="download-csv"
                            onClick={onDownloadCsvClick}
                            loading={isExportLoading}
                            disabled={isFetching}
                            menu={{
                                items: [
                                    {
                                        key: 1,
                                        label: 'Download Energy Usage Data',
                                        icon: <DownloadOutlined />,
                                        onClick: onEnergyUsageExport,
                                    },
                                ],
                                disabled: isExportLoading,
                            }}
                        >
                            <DownloadOutlined /> Download CSV
                        </Dropdown.Button>,
                    ]}
                />
                <EventAnalyticGroupedBySiteTableTitle batchId={batchId} companyId={pageState.companyId} />
                <WithEmptyDataTable condition={isFetching}>
                    <Table<IEventAnalyticGroupedBySite>
                        loading={isFetching}
                        columns={eventAnalyticColumns}
                        dataSource={eventAnalyticGroups}
                        onChange={onTableChange}
                        sticky={{ offsetHeader: 62 }}
                        size="small"
                        rowKey="site_id"
                        pagination={getTablePaginationParams(meta)}
                        components={{
                            header: {
                                cell: ResizableColumnTitle,
                            },
                        }}
                        expandable={{
                            expandedRowRender: record => (
                                <ExtraSiteAnalyticInfo
                                    eventAnalyticBySite={record}
                                    onChartOpen={meterLevelData => onChartViewOpen(record, meterLevelData)}
                                    onPerformanceHistoryChartOpen={meterLevelData => onHistoricalPerformanceViewOpen(record, meterLevelData)}
                                />
                            ),
                            rowExpandable: record => record.meters_count > 1,
                        }}
                    />
                </WithEmptyDataTable>
            </div>

            {csvDownloadPopupOpen && (
                <DownloadEventPerformanceCsvModal allowGroupByBatch={false} onClose={() => setCsvDownloadPopupOpen(false)} onSubmit={onCSVExport} />
            )}
            {chartPopupConfig && <PerformanceChartModal config={chartPopupConfig} onClose={() => setChartPopupConfig(null)} />}
            {historyChartPopupConfig && (
                <HistoricalPerformanceChartModal config={historyChartPopupConfig} onClose={() => setHistoryChartPopupConfig(null)} />
            )}
        </>
    );
};
